// Slide-over drawer hosting the document inspector, plus the per-document upload & review audit trail.
import React, { useEffect } from 'react';
import { History } from 'lucide-react';
import { fmtDateTime } from '../../rules';
import { eyebrow, Group } from './ui';

const LABEL = {
  DocumentRequested: 'Requested', DocumentUploaded: 'Uploaded by client', DocumentUploadedByBroker: 'Uploaded by staff', DocumentAccepted: 'Collected', DocumentNeedsReplacement: 'Re-upload requested',
  DocumentWaived: 'Marked not required', DocumentReopened: 'Re-opened', DocumentUnderReview: 'Moved to under review', DocumentReviewReset: 'Review reset', DocumentRequestRenamed: 'Renamed',
  DocumentReassigned: 'Reassigned', DocumentRecategorised: 'Category changed', DocumentFileRenamed: 'File renamed', DocumentFileDeleted: 'File deleted', DocumentAnalysed: 'AI analysis', DocumentViewed: 'Viewed', DocumentDownloaded: 'Downloaded', ReadinessItemsRequested: 'Client asked for outstanding items',
};

export const historyFor = (doc, events = []) => events.filter((e) => {
  const p = e.payload || {};
  if (e.type === 'ReadinessItemsRequested') return (p.items || []).some((i) => String(i).toLowerCase().includes((doc.name || '').toLowerCase()));
  if (!String(e.type || '').startsWith('Document')) return false;
  if (p.doc_id) return p.doc_id === doc.id;
  const nameHit = p.name === doc.name || p.to === doc.name || p.from === doc.name;
  return nameHit && (!e.applicant_id || !doc.applicant_id || e.applicant_id === doc.applicant_id || e.type === 'DocumentReassigned');
});

export function DocHistory({ items }) {
  return (
    <section data-testid="obd-doc-history">
      <p className={`${eyebrow} flex items-center gap-1.5`}><History className="h-3.5 w-3.5" /> History · {items.length}</p>
      {!items.length ? <p className="mt-2 text-[12px] text-[#86868b]">No activity recorded for this document yet.</p> : (
        <Group className="mt-2">
          {items.slice(0, 30).map((e) => { const p = e.payload || {}; const extra = p.file || p.notes || p.reason || (p.to && p.from ? `${p.from} → ${p.to}` : ''); return (
            <div key={e.id} className="flex items-start gap-3 border-b border-black/[0.05] px-4 py-2.5 last:border-b-0" data-testid={`obd-doc-history-${e.id}`}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#15133A]/40" />
              <div className="min-w-0 flex-1"><p className="text-[13px] font-medium text-[#1d1d1f]">{LABEL[e.type] || e.type.replace(/([A-Z])/g, ' $1').trim()}{extra ? <span className="font-normal text-[#6e6e73]"> · {String(extra).slice(0, 90)}</span> : null}</p><p className="mt-0.5 text-[11px] text-[#86868b]">{e.actor_name || 'System'} · {fmtDateTime(e.at)}</p></div>
            </div>); })}
        </Group>
      )}
    </section>
  );
}

export function DocDrawer({ open, onClose, children }) {
  useEffect(() => { if (!open) return undefined; const k = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" data-testid="obd-doc-drawer">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-[#0B0A1F]/30 backdrop-blur-[2px] animate-in fade-in duration-200" data-testid="obd-doc-drawer-backdrop" />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-[640px] flex-col overflow-y-auto bg-white shadow-[0_0_60px_-10px_rgba(15,23,42,0.35)] animate-in slide-in-from-right duration-300">{children}</div>
    </div>
  );
}
